import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import { useTranslation } from 'react-i18next';
import { useCart } from 'Context/CartContext';
import { useCountry } from 'Context/CountryContext';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import RelatedWatchesSlider from 'View/Component/RelatedWatchesSlider/RelatedWatchesSlider';
import * as S from './WatchDetailPage.style';

import authenticityBatch from 'Assets/authenticity_batch.png';
import secureShipmentBatch from 'Assets/secure_shipment_batch.png';
import warrantyBatch from 'Assets/warranty_batch.png';


const API_URL = process.env.REACT_APP_BACKEND_URL || '';

interface WatchImage {
  id: string;
  originalUrl: string;
  thumbnailUrl: string;
  watchId: string;
  altText?: string;
  displayOrder: number;
}

interface WatchDetail {
  id: string;
  brand: string;
  model: string;
  slug: string;
  shortCode?: string | null;
  priceEur: number | string;
  taxScheme?: 'MARGIN_25A_USTG' | 'STANDARD_VAT_19';
  appliedTaxPermille?: number | null;
  isExport?: boolean;
  priceCents: number | string;
  status: string;
  isAvailable?: boolean;
  isReserved?: boolean;
  isSold?: boolean;
  referenceNumber?: string;
  yearOfProduction?: number;
  description?: string;
  caseMaterial?: string;
  caseDiameterMm?: number;
  dialColor?: string;
  braceletMaterial?: string;
  movement?: string;
  caliber?: string;
  powerReserveHours?: number;
  hasBox?: boolean;
  hasPapers?: boolean;
  condition?: string;
  images?: WatchImage[];
  taxonomyOptions?: Array<{
    id: string;
    slug: string;
    value: string;
    displayOrder?: number;
    category?: { id: string; slug: string; name: string; displayOrder?: number };
  }>;
}

const WatchDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { country } = useCountry();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart, items } = useCart();
  const [watch, setWatch] = useState<WatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'details'>('details');
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartMessage, setCartMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isInCart, setIsInCart] = useState(false);

  useEffect(() => {
    fetchWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, i18n.language, country]);

  useEffect(() => {
    if (watch && items) {
      setIsInCart(items.some((item) => item.id === watch.id));
    }
  }, [watch, items]);

  const fetchWatch = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/watches/${slug}`);
      setWatch(response.data.watch);
    } catch (error) {
      console.error('Error fetching watch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!watch) return;

    setAddingToCart(true);
    setCartMessage(null);

    const result = await addToCart(watch.id);

    if (result.success) {
      setCartMessage({ type: 'success', text: result.message });
      setTimeout(() => setCartMessage(null), 20000);
    } else {
      setCartMessage({ type: 'error', text: result.message });
    }

    setAddingToCart(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <S.PageContainer>
          <div style={{ textAlign: 'center', padding: '4rem 0', fontFamily: 'Abel', fontSize: '1.2rem', color: '#657455' }}>
            {t('watch_detail.loading')}
          </div>
        </S.PageContainer>
        <Footer />
      </>
    );
  }

  if (!watch) {
    return (
      <>
        <Header />
        <S.PageContainer>
          <div style={{ textAlign: 'center', padding: '4rem 0', fontFamily: 'Abel', fontSize: '1.2rem', color: '#657455' }}>
            {t('watch_detail.not_found')}
          </div>
        </S.PageContainer>
        <Footer />
      </>
    );
  }

  const images = watch.images && watch.images.length > 0 ? watch.images : [];
  const currentImage = images[selectedImageIndex];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE';
  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(typeof watch.priceEur === 'string' ? parseFloat(watch.priceEur) : watch.priceEur);

  const conditionParts: string[] = [];
  if (watch.condition) conditionParts.push(watch.condition);
  if (watch.yearOfProduction) conditionParts.push(t('watch_detail.year_label', { year: watch.yearOfProduction }));
  if (watch.hasBox) conditionParts.push(t('watch_detail.with_box'));
  if (watch.hasPapers) conditionParts.push(t('watch_detail.with_papers'));

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.Breadcrumb>
          <Link to="/">{t('watch_detail.breadcrumb_home')}</Link>
          <span>›</span>
          <Link to="/kaufen">{t('watch_detail.breadcrumb_watches')}</Link>
          <span>›</span>
          <Link to={`/kaufen?brands=${encodeURIComponent(watch.brand)}`}>{watch.brand}</Link>
          <span>›</span>
          <span>{watch.model}</span>
        </S.Breadcrumb>

        <S.ContentGrid>
          <S.GallerySection>
            <S.MainImage>
              {currentImage ? (
                <img src={API_URL + currentImage.originalUrl} alt={currentImage?.altText ? currentImage.altText : `${watch.brand} ${watch.model}`} />
              ) : (
                <S.NoImagePlaceholder>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{t('watch_detail.no_image')}</span>
                </S.NoImagePlaceholder>
              )}

              {images.length > 1 && (
                <>
                  <S.NavArrow
                    side="left"
                    onClick={() => setSelectedImageIndex((p) => (p - 1 + images.length) % images.length)}
                    aria-label="Vorheriges Bild"
                    data-testid="gallery-prev-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </S.NavArrow>
                  <S.NavArrow
                    side="right"
                    onClick={() => setSelectedImageIndex((p) => (p + 1) % images.length)}
                    aria-label="Nächstes Bild"
                    data-testid="gallery-next-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </S.NavArrow>
                  <S.ImageCounter data-testid="gallery-counter">
                    {selectedImageIndex + 1} / {images.length}
                  </S.ImageCounter>
                </>
              )}
            </S.MainImage>

            {images.length > 1 && (
              <S.ThumbnailGrid>
                {images.map((img, index) => (
                  <S.Thumbnail
                    key={img.id}
                    $active={index === selectedImageIndex}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={API_URL + img.thumbnailUrl} alt={currentImage?.altText ? currentImage.altText : `${watch.brand} ${watch.model} ${index + 1}`} />
                  </S.Thumbnail>
                ))}
              </S.ThumbnailGrid>
            )}
          </S.GallerySection>

          <S.ProductInfo>
            <S.ProductTitle>
              <h1>{watch.brand} {watch.model}</h1>
              {watch.referenceNumber && <div className="reference">{watch.referenceNumber}</div>}
            </S.ProductTitle>

            {conditionParts.length > 0 && (
              <S.ProductMeta>
                {conditionParts.map((part, index) => (
                  <span key={index} className={index === 0 ? 'condition' : ''}>
                    {part}
                  </span>
                ))}
              </S.ProductMeta>
            )}

            <S.PriceSection>
              <div className="price">{formattedPrice}</div>
              <div className="vat-info" data-testid="pdp-tax-notice">
                {watch.isExport
                  ? t('watch_detail.tax_export', { defaultValue: 'Nettopreis (Ausfuhrlieferung) – Einfuhrumsatzsteuer/Zoll trägt der Käufer.' })
                  : watch.taxScheme === 'STANDARD_VAT_19'
                    ? t('watch_detail.tax_standard', { rate: ((watch.appliedTaxPermille ?? 190) / 10).toLocaleString(locale) })
                    : t('watch_detail.tax_margin')}
              </div>
              <div className="vat-info">{t('watch_detail.vat_info')}</div>
            </S.PriceSection>

            {watch.status === 'SOLD' && (
              <div
                data-testid="pdp-sold-banner"
                style={{
                  padding: '1rem 1.25rem',
                  borderRadius: '6px',
                  backgroundColor: '#212121',
                  color: '#fff',
                  fontFamily: 'Abel',
                  fontSize: '1rem',
                  letterSpacing: '0.08em',
                  textAlign: 'center',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                }}
              >
                {t('watch_detail.sold_banner')}
              </div>
            )}

            {watch.status === 'RESERVED' && (
              <div
                data-testid="pdp-reserved-banner"
                style={{
                  padding: '0.85rem 1.25rem',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(184, 150, 74, 0.12)',
                  color: '#7d6020',
                  border: '1px solid rgba(184, 150, 74, 0.4)',
                  fontFamily: 'Abel',
                  fontSize: '0.95rem',
                  marginBottom: '1rem',
                }}
              >
                {t('watch_detail.reserved_banner')}
              </div>
            )}

            <S.PaymentMethods>
              <div className="label">{t('watch_detail.payment_method_label')}</div>
              <div className="icon" title={t('watch_detail.payment_method')}>
                <img src="/assets/images/icons/sepa-bank.png" alt="SEPA" style={{ width: 30, height: 30, objectFit: 'contain', display: 'block' }} />
              </div>
              <span style={{ fontFamily: 'Abel', fontSize: '0.9rem', color: '#657455' }}>{t('watch_detail.payment_method')}</span>
            </S.PaymentMethods>

            {cartMessage && (
              <div style={{
                padding: '1rem',
                borderRadius: '6px',
                backgroundColor: cartMessage.type === 'success' ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${cartMessage.type === 'success' ? '#4caf50' : '#ff6b6b'}`,
                color: cartMessage.type === 'success' ? '#2e7d32' : '#c62828',
                fontFamily: 'Abel',
                fontSize: '0.95rem',
                marginBottom: '1rem',
              }}>
                {cartMessage.text}
                {cartMessage.type === 'success' && (
                  <S.CartLinkButton onClick={() => navigate('/warenkorb')}>
                    {t('watch_detail.to_cart_button')}
                  </S.CartLinkButton>
                )}
              </div>
            )}

            <S.PurchaseButton
              onClick={handlePurchase}
              disabled={addingToCart || isInCart || watch.status === 'SOLD' || watch.status === 'RESERVED'}
              data-testid="pdp-purchase-btn"
            >
              {watch.status === 'SOLD'
                ? t('watch_status.sold')
                : watch.status === 'RESERVED'
                ? t('watch_status.reserved')
                : addingToCart
                ? t('watch_detail.adding_to_cart')
                : isInCart
                ? t('watch_detail.already_in_cart')
                : t('watch_detail.add_to_cart')}
            </S.PurchaseButton>

            <S.InquiryButton
              type="button"
              onClick={() => {
                const qs = watch.shortCode ? `?watch=${encodeURIComponent(watch.shortCode)}` : '';
                navigate(`/kontakt${qs}`);
              }}
              data-testid="pdp-inquiry-btn"
            >
              {t('watch_detail.inquiry_button')}
            </S.InquiryButton>
            <S.InfoBox>
              <div className="title">{t('watch_detail.hint_delivery_time')}</div>
              <div style={{ fontFamily: 'Abel', fontSize: '0.95rem', color: '#657455', lineHeight: '1.6' }}>
                {t('watch_detail.description_delivery_time')}
              </div>

            </S.InfoBox>
            <S.BadgeRow>
              <S.Badge
                src={authenticityBatch}
                title={t('watch_detail.authenticity_batch')}
              />
              <S.Badge
                src={warrantyBatch}
                title={t('watch_detail.warranty_batch')}
              />
              <S.Badge
                src={secureShipmentBatch}
                title={t('watch_detail.secure_shipment_batch')}
              />
            </S.BadgeRow>
          </S.ProductInfo>


          <S.TabsSection>
            <S.TabHeader>
              <S.Tab $active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                {t('watch_detail.tab_details')}
              </S.Tab>
            </S.TabHeader>

            {activeTab === 'details' && (
              <S.TabContent>
                <S.DetailsColumn>
                  <div className="title">{t('watch_detail.section_base_data')}</div>
                  <DetailRow label={t('watch_detail.labels.brand')} value={watch.brand} />
                  <DetailRow label={t('watch_detail.labels.model')} value={watch.model} />
                  {watch.referenceNumber && <DetailRow label={t('watch_detail.labels.reference')} value={watch.referenceNumber} />}
                  {watch.movement && <DetailRow label={t('watch_detail.labels.movement')} value={watch.movement} />}
                  {watch.caliber && <DetailRow label={t('watch_detail.labels.caliber')} value={watch.caliber} />}
                  {watch.caseMaterial && <DetailRow label={t('watch_detail.labels.case_material')} value={watch.caseMaterial} />}
                  {watch.caseDiameterMm && <DetailRow label={t('watch_detail.labels.diameter')} value={`${watch.caseDiameterMm} ${t('watch_detail.units.mm')}`} />}
                  {watch.dialColor && <DetailRow label={t('watch_detail.labels.dial_color')} value={watch.dialColor} />}
                  {watch.braceletMaterial && <DetailRow label={t('watch_detail.labels.bracelet')} value={watch.braceletMaterial} />}
                  {watch.powerReserveHours && <DetailRow label={t('watch_detail.labels.power_reserve')} value={`${watch.powerReserveHours} ${t('watch_detail.units.hours')}`} />}
                  {watch.yearOfProduction && <DetailRow label={t('watch_detail.labels.year')} value={watch.yearOfProduction.toString()} />}
                  {watch.condition && <DetailRow label={t('watch_detail.labels.condition')} value={watch.condition} />}
                  <DetailRow label={t('watch_detail.labels.box')} value={watch.hasBox ? t('common.yes') : t('common.no')} />
                  <DetailRow label={t('watch_detail.labels.papers')} value={watch.hasPapers ? t('common.yes') : t('common.no')} />

                  {watch.taxonomyOptions && watch.taxonomyOptions.length > 0 && (() => {
                    const groups = new Map<string, { slug: string; name: string; order: number; values: Array<{ value: string; order: number }> }>();
                    watch.taxonomyOptions.forEach((opt) => {
                      if (!opt.category) return;
                      const k = opt.category.slug;
                      if (!groups.has(k)) {
                        groups.set(k, {
                          slug: k,
                          name: opt.category.name,
                          order: opt.category.displayOrder ?? 999,
                          values: [],
                        });
                      }
                      groups.get(k)!.values.push({ value: opt.value, order: opt.displayOrder ?? 999 });
                    });
                    // Order the rows by the category display order (as shown in the
                    // frontend taxonomy), and values within a category by their own order.
                    return Array.from(groups.values())
                      .sort((a, b) => a.order - b.order)
                      .map((g) => (
                        <DetailRow
                          key={g.slug}
                          label={g.name}
                          value={g.values
                            .sort((a, b) => a.order - b.order)
                            .map((v) => v.value)
                            .join(', ')}
                        />
                      ));
                  })()}
                </S.DetailsColumn>

                <S.DetailsColumn>
                  <div className="title">{t('watch_detail.section_description')}</div>
                  <S.Description>
                    {watch.description || t('watch_detail.no_description')}
                  </S.Description>
                </S.DetailsColumn>
              </S.TabContent>
            )}
          </S.TabsSection>
        </S.ContentGrid>
      </S.PageContainer>
      {watch?.brand && (
        <RelatedWatchesSlider brand={watch.brand} excludeId={watch.id} />
      )}
      <Footer />
    </>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <S.DetailRow data-testid="pdp-detail-row">
    <div className="label" data-testid="pdp-detail-label">{label}</div>
    <div className="value" data-testid="pdp-detail-value">{value}</div>
  </S.DetailRow>
);

export default WatchDetailPage;
